import { useEffect, useMemo, useState } from 'react';
import { EyeOff, FileText, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { normalizeProjectAiContext } from '@/lib/projectAiContext';

interface ProjectAiContextEditorProps {
  projectName: string;
  value?: string | null;
  onSave: (value: string | null) => Promise<void>;
}

const ProjectAiContextEditor = ({ projectName, value, onSave }: ProjectAiContextEditorProps) => {
  const [draft, setDraft] = useState(value ?? '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const normalizedDraft = useMemo(() => normalizeProjectAiContext(draft), [draft]);
  const normalizedValue = useMemo(() => normalizeProjectAiContext(value), [value]);
  const isDirty = normalizedDraft !== normalizedValue;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(normalizedDraft);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-border/60 bg-card/80 p-4 shadow-sm">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h2 className="text-xl font-semibold">Hidden AI markdown context</h2>
              <Badge variant="secondary" className="gap-1">
                <EyeOff className="h-3 w-3" />
                Hidden from users
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Add private markdown instructions or reference context for <span className="font-medium text-foreground">{projectName}</span>. This is injected into project chats behind the scenes and is not shown in the conversation UI.
            </p>
          </div>

          <Button onClick={handleSave} disabled={!isDirty || isSaving} className="sm:self-start">
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save context'}
          </Button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="project-ai-context">Markdown</Label>
          <Textarea
            id="project-ai-context"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={'# Product context\n- Audience: ...\n- Goal: ...\n- Constraints: ...'}
            className="min-h-[220px] font-mono text-sm"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{normalizedDraft ? `${normalizedDraft.length} characters saved for AI context` : 'No hidden markdown context saved yet'}</span>
          {isDirty && <span>Unsaved changes</span>}
        </div>
      </div>
    </Card>
  );
};

export default ProjectAiContextEditor;
